import { useEffect, useRef, useCallback, useState } from 'react';
import type { AppState, AppAction } from '@/lib/types';
import { createSheetsService, mergeData } from '@/lib/sheets-service';

export type SheetsStatus = 'idle' | 'syncing' | 'error' | 'paused';

const sheetsService = createSheetsService();

const TOKEN_CACHE_KEY = 'liftforge_token';
const MAX_RETRIES = 5;
const MAX_BACKOFF = 30000;

export function useSheetsSync(
  state: AppState,
  dispatch: (action: AppAction) => void,
) {
  const [sheetsStatus, setSheetsStatus] = useState<SheetsStatus>('idle');
  const retryCount = useRef(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevStateRef = useRef<AppState | null>(null);
  const initializedRef = useRef(false);
  const isSyncing = useRef(false);
  const lastActionRef = useRef<string | null>(null);

  // Expose a way for the reducer wrapper to signal which action just fired
  const setLastAction = useCallback((type: string) => {
    lastActionRef.current = type;
  }, []);

  const clearToken = useCallback(() => {
    dispatch({ type: 'SET_GAPI_TOKEN', token: null });
    try { sessionStorage.removeItem(TOKEN_CACHE_KEY); } catch {}
    sheetsService.accessToken = null;
    sheetsService.spreadsheetId = null;
    initializedRef.current = false;
    setSheetsStatus('idle');
  }, [dispatch]);

  const backoffDelay = useCallback(() => {
    const delay = Math.min(1000 * Math.pow(2, retryCount.current), MAX_BACKOFF);
    retryCount.current += 1;
    return delay;
  }, []);

  const handleApiError = useCallback((err: unknown) => {
    const msg = err instanceof Error ? err.message : String(err);
    const is401or403 = /401|403/.test(msg);

    if (is401or403) {
      clearToken();
      return;
    }

    if (retryCount.current >= MAX_RETRIES) {
      setSheetsStatus('paused');
      return;
    }

    setSheetsStatus('error');
  }, [clearToken]);

  // Save state to Sheets with error handling
  const saveToSheets = useCallback(async (stateToSave: AppState) => {
    if (!sheetsService.accessToken || !sheetsService.spreadsheetId) return;
    if (isSyncing.current) return;

    isSyncing.current = true;
    setSheetsStatus('syncing');
    try {
      await sheetsService.saveAll(stateToSave);
      retryCount.current = 0;
      setSheetsStatus('idle');
    } catch (err) {
      handleApiError(err);
      // Retry with backoff if not paused/auth-cleared
      if (retryCount.current < MAX_RETRIES && sheetsService.accessToken) {
        const delay = backoffDelay();
        setTimeout(() => saveToSheets(stateToSave), delay);
      }
    } finally {
      isSyncing.current = false;
    }
  }, [handleApiError, backoffDelay]);

  // Debounced save (1500ms)
  const debouncedSave = useCallback((stateToSave: AppState) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveToSheets(stateToSave);
    }, 1500);
  }, [saveToSheets]);

  // Initial connection when gapiToken is set
  useEffect(() => {
    if (!state.gapiToken) {
      initializedRef.current = false;
      sheetsService.accessToken = null;
      sheetsService.spreadsheetId = null;
      setSheetsStatus('idle');
      return;
    }

    if (initializedRef.current) return;
    initializedRef.current = true;

    const connect = async () => {
      sheetsService.accessToken = state.gapiToken;
      setSheetsStatus('syncing');

      try {
        let id = await sheetsService.findSpreadsheet();
        if (!id) id = await sheetsService.createSpreadsheet();

        sheetsService.spreadsheetId = id;
        dispatch({ type: 'SET_SPREADSHEET_ID', id });

        const remoteData = await sheetsService.loadAll();

        // Merge remote with local state
        const merged = mergeData(
          state,
          remoteData as AppState,
          state.configTimestamp,
          (remoteData as any).configTimestamp || 0,
        );

        dispatch({ type: 'LOAD_FROM_SHEETS', data: merged });
        prevStateRef.current = { ...state, ...merged };

        // If local had changes, push merged result back
        if (state.history.length > 0 || Object.keys(state.workouts).length > 0) {
          await sheetsService.saveAll({ ...state, ...merged });
        }

        retryCount.current = 0;
        setSheetsStatus('idle');

        // Cache token in sessionStorage for faster reconnect
        try {
          sessionStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify({
            token: state.gapiToken,
            ssId: id,
            expiresAt: Date.now() + 3500000,
          }));
        } catch {}
      } catch (err) {
        handleApiError(err);
      }
    };

    connect();
  }, [state.gapiToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch state changes and sync
  useEffect(() => {
    if (!sheetsService.accessToken || !sheetsService.spreadsheetId) return;
    if (!prevStateRef.current) {
      prevStateRef.current = state;
      return;
    }
    if (state === prevStateRef.current) return;

    const lastAction = lastActionRef.current;
    lastActionRef.current = null;

    // Skip syncing actions that don't contain user data changes
    if (
      lastAction === 'SET_GAPI_TOKEN' ||
      lastAction === 'SET_SPREADSHEET_ID' ||
      lastAction === 'LOAD_FROM_SHEETS'
    ) {
      prevStateRef.current = state;
      return;
    }

    // During active tracking, only sync on SAVE_WORKOUT
    if (state.trackingSession !== null && lastAction !== 'SAVE_WORKOUT') {
      prevStateRef.current = state;
      return;
    }

    // If we just saved a workout (SAVE_WORKOUT), sync immediately
    if (lastAction === 'SAVE_WORKOUT') {
      prevStateRef.current = state;
      saveToSheets(state);
      return;
    }

    prevStateRef.current = state;
    debouncedSave(state);
  }, [state, debouncedSave, saveToSheets]);

  // Manual sync function
  const manualSync = useCallback(async () => {
    if (!sheetsService.accessToken || !sheetsService.spreadsheetId) return;
    setSheetsStatus('syncing');
    isSyncing.current = true;
    try {
      await sheetsService.saveAll(state);
      const remoteData = await sheetsService.loadAll();
      const merged = mergeData(
        state,
        remoteData as AppState,
        state.configTimestamp,
        (remoteData as any).configTimestamp || 0,
      );
      dispatch({ type: 'LOAD_FROM_SHEETS', data: merged });
      prevStateRef.current = { ...state, ...merged };
      retryCount.current = 0;
      setSheetsStatus('idle');
    } catch (err) {
      handleApiError(err);
    } finally {
      isSyncing.current = false;
    }
  }, [state, dispatch, handleApiError]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  return { sheetsStatus, manualSync, setLastAction };
}
